// src/lib/black-friday-data-utils.ts
import {
  BFStorageData,
  BFDashboardMetrics,
  BFProductData,
  BFSearchTermData,
  BFPMaxCategoryData,
  BFDailyCampaignData,
  BFChannelData,
  BFSubChannelData,
} from "@/types/black-friday";

const DEBUG = process.env.NODE_ENV === "development";

// Dev fallback URL for testing
export const DEV_FALLBACK_BF_WEB_APP_URL =
  "https://script.google.com/macros/s/AKfycbztQDaglt2CMgVgBoVTTWEOcUPWmHcQCa2txi-MuRxlPmbw8YlVVuNQWwjoLndvdyhD/exec";

// Sheet tab names matching the Google Apps Script output
export const BF_SHEET_TABS = {
  DASHBOARD: "dashboard",
  PRODUCTS_25: "rProd25", // 2025 products (year1 from script)
  PRODUCTS_24: "rProd24", // 2024 products (year2 from script)
  PRODUCTS_23: "rProd23", // 2023 products (year3 from script)
  SEARCH_TERMS_25: "rSearch25", // 2025 search terms
  SEARCH_TERMS_24: "rSearch24", // 2024 search terms
  SEARCH_TERMS_23: "rSearch23", // 2023 search terms
  PMAX_CATEGORIES_25: "rPMaxCat25", // 2025 PMax categories
  PMAX_CATEGORIES_24: "rPMaxCat24", // 2024 PMax categories
  PMAX_CATEGORIES_23: "rPMaxCat23", // 2023 PMax categories
  CAMPAIGN_25: "rCamp25", // 2025 campaign data
  CAMPAIGN_24: "rCamp24", // 2024 campaign data
  CAMPAIGN_23: "rCamp23", // 2023 campaign data
  ALL_CAMPAIGNS_25: "rAll25", // 2025 all campaigns
  ALL_CAMPAIGNS_24: "rAll24", // 2024 all campaigns
  ALL_CAMPAIGNS_23: "rAll23", // 2023 all campaigns
  CHANNEL: "rChannel",
  SUBCHANNEL: "rSub",
} as const;

// Data transformers for each sheet tab
const bfTransformers = {
  [BF_SHEET_TABS.DASHBOARD]: (data: any[]): BFDashboardMetrics => {
    // Dashboard data comes as named ranges, different structure
    // For now, return a simple object, we'll enhance this based on actual data structure
    if (!data || data.length === 0) {
      return {
        accountCID: "",
        accountName: "",
        highestCpcKeyword: "",
        highestCpcKeywordCpc: 0,
        highestCpcSearchTerm: "",
        highestCpcSearchTermCpc: 0,
        exactCost: 0,
        exactConv: 0,
        phraseCost: 0,
        phraseConv: 0,
        broadCost: 0,
        broadConv: 0,
        ngramSearchTerms: 0,
        ngramUniqueWords: 0,
        ngramNewWords: 0,
        ngramNewWordsCost: 0,
        campaignNegatives: 0,
        adGroupNegatives: 0,
        negativeLists: 0,
        totalNegativesInLists: 0,
      };
    }

    const row = data[0] || {};
    return {
      accountCID: String(row.accountCID || ""),
      accountName: String(row.accountName || ""),
      scriptVersion: String(row.scriptVersion || ""),
      highestCpcKeyword: String(row.highKey || row.highestCpcKeyword || ""),
      highestCpcKeywordCpc: Number(row.highKeyCpc || row.highestCpcKeywordCpc || 0),
      highestCpcSearchTerm: String(row.highSearch || row.highestCpcSearchTerm || ""),
      highestCpcSearchTermCpc: Number(row.highSearchCpc || row.highestCpcSearchTermCpc || 0),
      exactCost: Number(row.exactCost || 0),
      exactConv: Number(row.exactConv || 0),
      phraseCost: Number(row.phraseCost || 0),
      phraseConv: Number(row.phraseConv || 0),
      broadCost: Number(row.broadCost || 0),
      broadConv: Number(row.broadConv || 0),
      ngramSearchTerms: Number(row.ngramTerms || row.ngramSearchTerms || 0),
      ngramUniqueWords: Number(row.ngramWords || row.ngramUniqueWords || 0),
      ngramNewWords: Number(row.newWords || row.ngramNewWords || 0),
      ngramNewWordsCost: Number(row.newCost || row.ngramNewWordsCost || 0),
      campaignNegatives: Number(row.campNeg || row.campaignNegatives || 0),
      adGroupNegatives: Number(row.adNeg || row.adGroupNegatives || 0),
      negativeLists: Number(row.listNeg || row.negativeLists || 0),
      totalNegativesInLists: Number(row.totalNeg || row.totalNegativesInLists || 0),
    };
  },

  [BF_SHEET_TABS.PRODUCTS_25]: (data: any[]): BFProductData[] =>
    data.map((row) => ({
      productId: String(row.productId || row["Product ID"] || ""),
      productTitle: String(row.productTitle || row["Product Title"] || ""),
      impressions: Number(row.impressions || row.Impressions || 0),
      clicks: Number(row.clicks || row.Clicks || 0),
      cost: Number(row.cost || row.Cost || 0),
      conv: Number(row.conv || row.Conv || 0),
      convValue: Number(row.convValue || row["Conv Val"] || 0),
    })),

  [BF_SHEET_TABS.PRODUCTS_24]: (data: any[]): BFProductData[] =>
    data.map((row) => ({
      productId: String(row.productId || row["Product ID"] || ""),
      productTitle: String(row.productTitle || row["Product Title"] || ""),
      impressions: Number(row.impressions || row.Impressions || 0),
      clicks: Number(row.clicks || row.Clicks || 0),
      cost: Number(row.cost || row.Cost || 0),
      conv: Number(row.conv || row.Conv || 0),
      convValue: Number(row.convValue || row["Conv Val"] || 0),
    })),

  [BF_SHEET_TABS.PRODUCTS_23]: (data: any[]): BFProductData[] =>
    data.map((row) => ({
      productId: String(row.productId || row["Product ID"] || ""),
      productTitle: String(row.productTitle || row["Product Title"] || ""),
      impressions: Number(row.impressions || row.Impressions || 0),
      clicks: Number(row.clicks || row.Clicks || 0),
      cost: Number(row.cost || row.Cost || 0),
      conv: Number(row.conv || row.Conv || 0),
      convValue: Number(row.convValue || row["Conv Val"] || 0),
    })),

  [BF_SHEET_TABS.SEARCH_TERMS_25]: (data: any[]): BFSearchTermData[] =>
    data.map((row) => ({
      campaignName: String(row["Campaign Name"] || row.campaignName || ""),
      campaignId: String(row["Campaign ID"] || row.campaignId || ""),
      channelType: String(row["Channel Type"] || row.channelType || ""),
      searchTerm: String(row["Search Term"] || row.searchTerm || ""),
      clicks: Number(row.Clicks || row.clicks || 0),
      impressions: Number(row.Impr || row.impressions || 0),
      conv: Number(row.Conv || row.conv || 0),
      convValue: Number(row["Conv Val"] || row.convValue || 0),
      cost: Number(row.Cost || row.cost || 0),
    })),

  [BF_SHEET_TABS.SEARCH_TERMS_24]: (data: any[]): BFSearchTermData[] =>
    data.map((row) => ({
      campaignName: String(row["Campaign Name"] || row.campaignName || ""),
      campaignId: String(row["Campaign ID"] || row.campaignId || ""),
      channelType: String(row["Channel Type"] || row.channelType || ""),
      searchTerm: String(row["Search Term"] || row.searchTerm || ""),
      clicks: Number(row.Clicks || row.clicks || 0),
      impressions: Number(row.Impr || row.impressions || 0),
      conv: Number(row.Conv || row.conv || 0),
      convValue: Number(row["Conv Val"] || row.convValue || 0),
      cost: Number(row.Cost || row.cost || 0),
    })),

  [BF_SHEET_TABS.SEARCH_TERMS_23]: (data: any[]): BFSearchTermData[] =>
    data.map((row) => ({
      campaignName: String(row["Campaign Name"] || row.campaignName || ""),
      campaignId: String(row["Campaign ID"] || row.campaignId || ""),
      channelType: String(row["Channel Type"] || row.channelType || ""),
      searchTerm: String(row["Search Term"] || row.searchTerm || ""),
      clicks: Number(row.Clicks || row.clicks || 0),
      impressions: Number(row.Impr || row.impressions || 0),
      conv: Number(row.Conv || row.conv || 0),
      convValue: Number(row["Conv Val"] || row.convValue || 0),
      cost: Number(row.Cost || row.cost || 0),
    })),

  [BF_SHEET_TABS.PMAX_CATEGORIES_25]: (data: any[]): BFPMaxCategoryData[] =>
    data.map((row) => ({
      campaignName: String(row["Campaign Name"] || row.campaignName || ""),
      campaignId: String(row["Campaign ID"] || row.campaignId || ""),
      channelType: String(row["Channel Type"] || row.channelType || "PERFORMANCE_MAX"),
      categoryLabel: String(row["Category Label"] || row.categoryLabel || ""),
      clicks: Number(row.Clicks || row.clicks || 0),
      impressions: Number(row.Impr || row.impressions || 0),
      conv: Number(row.Conv || row.conv || 0),
      convValue: Number(row["Conv Val"] || row.convValue || 0),
    })),

  [BF_SHEET_TABS.PMAX_CATEGORIES_24]: (data: any[]): BFPMaxCategoryData[] =>
    data.map((row) => ({
      campaignName: String(row["Campaign Name"] || row.campaignName || ""),
      campaignId: String(row["Campaign ID"] || row.campaignId || ""),
      channelType: String(row["Channel Type"] || row.channelType || "PERFORMANCE_MAX"),
      categoryLabel: String(row["Category Label"] || row.categoryLabel || ""),
      clicks: Number(row.Clicks || row.clicks || 0),
      impressions: Number(row.Impr || row.impressions || 0),
      conv: Number(row.Conv || row.conv || 0),
      convValue: Number(row["Conv Val"] || row.convValue || 0),
    })),

  [BF_SHEET_TABS.PMAX_CATEGORIES_23]: (data: any[]): BFPMaxCategoryData[] =>
    data.map((row) => ({
      campaignName: String(row["Campaign Name"] || row.campaignName || ""),
      campaignId: String(row["Campaign ID"] || row.campaignId || ""),
      channelType: String(row["Channel Type"] || row.channelType || "PERFORMANCE_MAX"),
      categoryLabel: String(row["Category Label"] || row.categoryLabel || ""),
      clicks: Number(row.Clicks || row.clicks || 0),
      impressions: Number(row.Impr || row.impressions || 0),
      conv: Number(row.Conv || row.conv || 0),
      convValue: Number(row["Conv Val"] || row.convValue || 0),
    })),

  [BF_SHEET_TABS.CAMPAIGN_25]: (data: any[]): BFDailyCampaignData[] =>
    data.map((row) => ({
      date: String(row.date || ""),
      index: Number(row.index || 0),
      campaign: String(row.campaign || ""),
      impr: Number(row.Impr || row.impr || 0),
      clicks: Number(row.Clicks || row.clicks || 0),
      cost: Number(row.Cost || row.cost || 0),
      conv: Number(row.Conv || row.conv || 0),
      convValue: Number(row["Conv Val"] || row.convValue || 0),
      allConv: Number(row["All Conv"] || row.allConv || 0),
      allConvValue: Number(row["All Conv Val"] || row.allConvValue || 0),
      convDate: Number(row["Conv Date"] || row.convDate || 0),
      convValueDate: Number(row["Conv Val Date"] || row.convValueDate || 0),
      views: Number(row.Views || row.views || 0),
      CPC: Number(row.CPC || 0),
      CTR: Number(row.CTR || 0),
      CPA: Number(row.CPA || 0),
      CvR: Number(row.CvR || 0),
      ROAS: Number(row.ROAS || 0),
      AOV: Number(row.AOV || 0),
      camptype: String(row.camptype || ""),
    })),

  [BF_SHEET_TABS.CAMPAIGN_24]: (data: any[]): BFDailyCampaignData[] =>
    data.map((row) => ({
      date: String(row.date || ""),
      index: Number(row.index || 0),
      campaign: String(row.campaign || ""),
      impr: Number(row.Impr || row.impr || 0),
      clicks: Number(row.Clicks || row.clicks || 0),
      cost: Number(row.Cost || row.cost || 0),
      conv: Number(row.Conv || row.conv || 0),
      convValue: Number(row["Conv Val"] || row.convValue || 0),
      allConv: Number(row["All Conv"] || row.allConv || 0),
      allConvValue: Number(row["All Conv Val"] || row.allConvValue || 0),
      convDate: Number(row["Conv Date"] || row.convDate || 0),
      convValueDate: Number(row["Conv Val Date"] || row.convValueDate || 0),
      views: Number(row.Views || row.views || 0),
      CPC: Number(row.CPC || 0),
      CTR: Number(row.CTR || 0),
      CPA: Number(row.CPA || 0),
      CvR: Number(row.CvR || 0),
      ROAS: Number(row.ROAS || 0),
      AOV: Number(row.AOV || 0),
      camptype: String(row.camptype || ""),
    })),

  [BF_SHEET_TABS.CAMPAIGN_23]: (data: any[]): BFDailyCampaignData[] =>
    data.map((row) => ({
      date: String(row.date || ""),
      index: Number(row.index || 0),
      campaign: String(row.campaign || ""),
      impr: Number(row.Impr || row.impr || 0),
      clicks: Number(row.Clicks || row.clicks || 0),
      cost: Number(row.Cost || row.cost || 0),
      conv: Number(row.Conv || row.conv || 0),
      convValue: Number(row["Conv Val"] || row.convValue || 0),
      allConv: Number(row["All Conv"] || row.allConv || 0),
      allConvValue: Number(row["All Conv Val"] || row.allConvValue || 0),
      convDate: Number(row["Conv Date"] || row.convDate || 0),
      convValueDate: Number(row["Conv Val Date"] || row.convValueDate || 0),
      views: Number(row.Views || row.views || 0),
      CPC: Number(row.CPC || 0),
      CTR: Number(row.CTR || 0),
      CPA: Number(row.CPA || 0),
      CvR: Number(row.CvR || 0),
      ROAS: Number(row.ROAS || 0),
      AOV: Number(row.AOV || 0),
      camptype: String(row.camptype || ""),
    })),

  [BF_SHEET_TABS.ALL_CAMPAIGNS_25]: (data: any[]): BFDailyCampaignData[] =>
    data.map((row) => ({
      date: String(row.date || ""),
      index: Number(row.index || 0),
      campaign: "All Campaigns",
      impr: Number(row.Impr || row.impr || 0),
      clicks: Number(row.Clicks || row.clicks || 0),
      cost: Number(row.Cost || row.cost || 0),
      conv: Number(row.Conv || row.conv || 0),
      convValue: Number(row["Conv Val"] || row.convValue || 0),
      allConv: Number(row["All Conv"] || row.allConv || 0),
      allConvValue: Number(row["All Conv Val"] || row.allConvValue || 0),
      convDate: Number(row["Conv Date"] || row.convDate || 0),
      convValueDate: Number(row["Conv Val Date"] || row.convValueDate || 0),
      views: Number(row.Views || row.views || 0),
      CPC: Number(row.CPC || 0),
      CTR: Number(row.CTR || 0),
      CPA: Number(row.CPA || 0),
      CvR: Number(row.CvR || 0),
      ROAS: Number(row.ROAS || 0),
      AOV: Number(row.AOV || 0),
      camptype: "All Campaigns",
    })),

  [BF_SHEET_TABS.ALL_CAMPAIGNS_24]: (data: any[]): BFDailyCampaignData[] =>
    data.map((row) => ({
      date: String(row.date || ""),
      index: Number(row.index || 0),
      campaign: "All Campaigns",
      impr: Number(row.Impr || row.impr || 0),
      clicks: Number(row.Clicks || row.clicks || 0),
      cost: Number(row.Cost || row.cost || 0),
      conv: Number(row.Conv || row.conv || 0),
      convValue: Number(row["Conv Val"] || row.convValue || 0),
      allConv: Number(row["All Conv"] || row.allConv || 0),
      allConvValue: Number(row["All Conv Val"] || row.allConvValue || 0),
      convDate: Number(row["Conv Date"] || row.convDate || 0),
      convValueDate: Number(row["Conv Val Date"] || row.convValueDate || 0),
      views: Number(row.Views || row.views || 0),
      CPC: Number(row.CPC || 0),
      CTR: Number(row.CTR || 0),
      CPA: Number(row.CPA || 0),
      CvR: Number(row.CvR || 0),
      ROAS: Number(row.ROAS || 0),
      AOV: Number(row.AOV || 0),
      camptype: "All Campaigns",
    })),

  [BF_SHEET_TABS.ALL_CAMPAIGNS_23]: (data: any[]): BFDailyCampaignData[] =>
    data.map((row) => ({
      date: String(row.date || ""),
      index: Number(row.index || 0),
      campaign: "All Campaigns",
      impr: Number(row.Impr || row.impr || 0),
      clicks: Number(row.Clicks || row.clicks || 0),
      cost: Number(row.Cost || row.cost || 0),
      conv: Number(row.Conv || row.conv || 0),
      convValue: Number(row["Conv Val"] || row.convValue || 0),
      allConv: Number(row["All Conv"] || row.allConv || 0),
      allConvValue: Number(row["All Conv Val"] || row.allConvValue || 0),
      convDate: Number(row["Conv Date"] || row.convDate || 0),
      convValueDate: Number(row["Conv Val Date"] || row.convValueDate || 0),
      views: Number(row.Views || row.views || 0),
      CPC: Number(row.CPC || 0),
      CTR: Number(row.CTR || 0),
      CPA: Number(row.CPA || 0),
      CvR: Number(row.CvR || 0),
      ROAS: Number(row.ROAS || 0),
      AOV: Number(row.AOV || 0),
      camptype: "All Campaigns",
    })),

  [BF_SHEET_TABS.CHANNEL]: (data: any[]): BFChannelData[] =>
    data.map((row) => {
      const result: BFChannelData = {
        date: String(row.Date || row.date || ""),
        index: Number(row.Index || row.index || 0),
      };

      // Dynamically add all channel metrics
      Object.keys(row).forEach((key) => {
        if (key !== "Date" && key !== "date" && key !== "Index" && key !== "index") {
          result[key] = row[key];
        }
      });

      return result;
    }),

  [BF_SHEET_TABS.SUBCHANNEL]: (data: any[]): BFSubChannelData[] =>
    data.map((row) => {
      const result: BFSubChannelData = {
        date: String(row.Date || row.date || ""),
        index: Number(row.Index || row.index || 0),
      };

      // Dynamically add all sub-channel metrics
      Object.keys(row).forEach((key) => {
        if (key !== "Date" && key !== "date" && key !== "Index" && key !== "index") {
          result[key] = row[key];
        }
      });

      return result;
    }),
} as const;

/**
 * Fetch only dashboard metadata (accountCID, accountName, scriptVersion)
 * This is much faster than fetching all data - useful for displaying account info
 * @param webAppUrl - The deployed web app URL from Google Apps Script
 * @returns Dashboard metadata only
 */
export async function fetchBFDashboardInfo(
  webAppUrl: string,
): Promise<BFDashboardMetrics> {
  try {
    const response = await fetch(`${webAppUrl}?tab=${BF_SHEET_TABS.DASHBOARD}`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const responseText = await response.text();
    let data;

    try {
      data = JSON.parse(responseText);
    } catch (e) {
      throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}`);
    }

    if (!Array.isArray(data)) {
      throw new Error("Invalid data format - expected array");
    }

    if (data.length === 1 && data[0]?.error) {
      throw new Error(data[0].error);
    }

    // Transform data using dashboard transformer
    const transformer = bfTransformers[BF_SHEET_TABS.DASHBOARD];
    const dashboardData: BFDashboardMetrics = transformer(data);

    if (DEBUG) {
      console.log(`[BF] Fetched dashboard info:`, {
        accountCID: dashboardData.accountCID,
        accountName: dashboardData.accountName,
        scriptVersion: dashboardData.scriptVersion,
      });
    }

    return dashboardData;
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : "Unknown error";
    console.error(`[BF] Failed to fetch dashboard info:`, errorMsg);
    throw err;
  }
}

/**
 * Fetch all Black Friday dashboard data from Google Sheets Web App
 * @param webAppUrl - The deployed web app URL from Google Apps Script
 * @param dexieId - Optional ID for IndexedDB storage
 * @returns Complete BF dashboard data
 */
export async function fetchAllBFData(
  webAppUrl: string,
  dexieId?: string,
): Promise<BFStorageData> {
  let effectiveWebAppUrl = webAppUrl;
  if (DEBUG && !effectiveWebAppUrl) {
    console.warn(
      "[BLACK FRIDAY] No webAppUrl provided in dev mode. Using fallback URL.",
    );
    effectiveWebAppUrl = DEV_FALLBACK_BF_WEB_APP_URL;
  }

  if (DEBUG) {
    console.log("[BLACK FRIDAY] Fetching all data from:", effectiveWebAppUrl);
  }
  console.log("[BLACK FRIDAY] Starting fetch at:", new Date().toISOString());

  const storageData: Partial<BFStorageData> = {
    webAppUrl,
    dexieId,
    timestamp: new Date().toISOString(),
  };

  // Fetch all tabs in parallel
  const fetchPromises = Object.entries(BF_SHEET_TABS).map(
    async ([tabKey, tabName]) => {
      try {
        const response = await fetch(`${effectiveWebAppUrl}?tab=${tabName}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const responseText = await response.text();
        let data;

        try {
          data = JSON.parse(responseText);
        } catch (e) {
          if (responseText.trim().toLowerCase().startsWith("success")) {
            if (DEBUG) {
              console.warn(
                `[BLACK FRIDAY] Received success string for tab ${tabName}. Treating as empty data.`,
              );
            }
            data = [];
          } else {
            throw new Error(
              `Invalid JSON response for tab ${tabName}: ${responseText.substring(0, 100)}`,
            );
          }
        }

        if (!Array.isArray(data)) {
          throw new Error("Invalid data format - expected array");
        }

        if (data.length === 1 && data[0]?.error) {
          throw new Error(data[0].error);
        }

        // Transform data using appropriate transformer
        const transformer = bfTransformers[tabName as keyof typeof bfTransformers];
        const transformedData = transformer ? transformer(data) : data;

        /*
        if (DEBUG) {
          console.log(`[BLACK FRIDAY] Loaded ${tabName}:`, {
            raw: data.length,
            transformed: Array.isArray(transformedData) ? transformedData.length : 'single object',
          });
        }
        */
        return { tabKey, tabName, data: transformedData, success: true };
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Unknown error";
        if (DEBUG) {
          console.error(
            `[BLACK FRIDAY] Failed to fetch tab ${tabName}:`,
            errorMsg,
          );
        }
        return { tabKey, tabName, data: null, success: false, error: errorMsg };
      }
    },
  );

  const results = await Promise.all(fetchPromises);

  // Map results to storage structure
  results.forEach((result) => {
    if (result.success && result.data) {
      switch (result.tabName) {
        case BF_SHEET_TABS.DASHBOARD:
          storageData.dashboard = result.data as BFDashboardMetrics;
          break;
        case BF_SHEET_TABS.PRODUCTS_25:
          storageData.products25 = result.data as BFProductData[];
          break;
        case BF_SHEET_TABS.PRODUCTS_24:
          storageData.products24 = result.data as BFProductData[];
          break;
        case BF_SHEET_TABS.PRODUCTS_23:
          storageData.products23 = result.data as BFProductData[];
          break;
        case BF_SHEET_TABS.SEARCH_TERMS_25:
          storageData.searchTerms25 = result.data as BFSearchTermData[];
          break;
        case BF_SHEET_TABS.SEARCH_TERMS_24:
          storageData.searchTerms24 = result.data as BFSearchTermData[];
          break;
        case BF_SHEET_TABS.SEARCH_TERMS_23:
          storageData.searchTerms23 = result.data as BFSearchTermData[];
          break;
        case BF_SHEET_TABS.PMAX_CATEGORIES_25:
          storageData.pMaxCategories25 = result.data as BFPMaxCategoryData[];
          break;
        case BF_SHEET_TABS.PMAX_CATEGORIES_24:
          storageData.pMaxCategories24 = result.data as BFPMaxCategoryData[];
          break;
        case BF_SHEET_TABS.PMAX_CATEGORIES_23:
          storageData.pMaxCategories23 = result.data as BFPMaxCategoryData[];
          break;
        case BF_SHEET_TABS.CAMPAIGN_25:
          storageData.dailyCampaign25 = result.data as BFDailyCampaignData[];
          break;
        case BF_SHEET_TABS.CAMPAIGN_24:
          storageData.dailyCampaign24 = result.data as BFDailyCampaignData[];
          break;
        case BF_SHEET_TABS.CAMPAIGN_23:
          storageData.dailyCampaign23 = result.data as BFDailyCampaignData[];
          break;
        case BF_SHEET_TABS.ALL_CAMPAIGNS_25:
          storageData.dailyAllCampaigns25 = result.data as BFDailyCampaignData[];
          break;
        case BF_SHEET_TABS.ALL_CAMPAIGNS_24:
          storageData.dailyAllCampaigns24 = result.data as BFDailyCampaignData[];
          break;
        case BF_SHEET_TABS.ALL_CAMPAIGNS_23:
          storageData.dailyAllCampaigns23 = result.data as BFDailyCampaignData[];
          break;
        case BF_SHEET_TABS.CHANNEL:
          storageData.dailyChannel = result.data as BFChannelData[];
          break;
        case BF_SHEET_TABS.SUBCHANNEL:
          storageData.dailySubChannel = result.data as BFSubChannelData[];
          break;
      }
    }
  });

  if (DEBUG) {
    console.log("[BLACK FRIDAY] Fetch complete:", {
      dashboard: !!storageData.dashboard,
      products25: storageData.products25?.length || 0,
      products24: storageData.products24?.length || 0,
      products23: storageData.products23?.length || 0,
      dailyCampaign25: storageData.dailyCampaign25?.length || 0,
      dailyCampaign24: storageData.dailyCampaign24?.length || 0,
      dailyCampaign23: storageData.dailyCampaign23?.length || 0,
      dailyAllCampaigns25: storageData.dailyAllCampaigns25?.length || 0,
      dailyAllCampaigns24: storageData.dailyAllCampaigns24?.length || 0,
      dailyAllCampaigns23: storageData.dailyAllCampaigns23?.length || 0,
      dailyChannel: storageData.dailyChannel?.length || 0,
      dailySubChannel: storageData.dailySubChannel?.length || 0,
    });
  }

  return storageData as BFStorageData;
}

/**
 * Get unique campaign names from daily campaign data with costs
 * Used for campaign selector dropdown
 */
export function getCampaignOptions(
  dailyCampaignData: BFDailyCampaignData[],
): { value: string; label: string; cost?: number }[] {
  // Calculate total cost for each campaign
  const campaignCosts = new Map<string, number>();

  dailyCampaignData.forEach((row) => {
    if (row.campaign && row.campaign !== "All Campaigns") {
      const currentCost = campaignCosts.get(row.campaign) || 0;
      campaignCosts.set(row.campaign, currentCost + row.cost);
    }
  });

  // Convert to array and sort by cost (descending)
  const campaignList = Array.from(campaignCosts.entries())
    .sort((a, b) => b[1] - a[1]) // Sort by cost descending
    .map(([campaign, cost]) => ({
      value: campaign,
      label: `${campaign}   $${Math.round(cost).toLocaleString()}`,
      cost,
    }));

  const options = [
    { value: "All Campaigns", label: "All Campaigns" },
    ...campaignList,
  ];

  return options;
}

/**
 * Normalize campaign type to standard field prefix
 */
function normalizeCampaignType(camptype: string): string {
  const normalized = camptype.toUpperCase().replace(/\s+/g, "_");

  // Map common variations to standard names
  const typeMap: Record<string, string> = {
    'PERFORMANCE_MAX': 'PERFORMANCE_MAX',
    'PMAX': 'PERFORMANCE_MAX',
    'SEARCH': 'SEARCH',
    'SHOPPING': 'SHOPPING',
    'DISPLAY': 'DISPLAY',
    'VIDEO': 'VIDEO',
    'DEMAND_GEN': 'DEMAND_GEN',
    'DEMAND': 'DEMAND_GEN',
  };

  return typeMap[normalized] || normalized;
}

/**
 * Aggregate campaign data by channel type to create channel-level data
 * This is needed when the script doesn't provide separate channel data
 */
export function aggregateChannelData(
  dailyCampaignData: BFDailyCampaignData[],
): BFChannelData[] {
  const DEBUG = process.env.NODE_ENV === "development";

  // Group by date/index and campaign type
  const grouped = new Map<number, BFChannelData>();

  dailyCampaignData.forEach((row) => {
    if (!row.camptype) return;

    const index = row.index;
    if (!grouped.has(index)) {
      grouped.set(index, {
        date: row.date,
        index: row.index,
      });
    }

    const channelRow = grouped.get(index)!;
    const typePrefix = normalizeCampaignType(row.camptype);

    // Store cost with the format expected by the chart: "PERFORMANCE_MAX Cost" (space + capital C)
    const costKey = `${typePrefix} Cost`;
    channelRow[costKey] = (channelRow[costKey] as number || 0) + row.cost;
  });

  const result = Array.from(grouped.values()).sort((a, b) => a.index - b.index);

  /*
  // Debug logging
  if (DEBUG && result.length > 0) {
    console.log('[BF Channel Aggregation] Sample row:', result[0]);
    console.log('[BF Channel Aggregation] Total rows:', result.length);
  }
  */
 
  return result;
}

// Helper function to parse "Impr" field which may be abbreviated (e.g., "1.2k" → 1200)
function parseImpressions(value: string | number): number {
  if (typeof value === 'number') return value;
  if (!value) return 0;

  const str = String(value).toLowerCase();
  if (str.endsWith('k')) {
    return parseFloat(str) * 1000;
  } else if (str.endsWith('m')) {
    return parseFloat(str) * 1000000;
  }
  return parseFloat(str) || 0;
}
